import { ReactComponent as IconChecked } from "@/assets/svgs/checkbox-checked.svg";
import { ReactComponent as IconPartialChecked } from "@/assets/svgs/checkbox-partial-checked.svg";
import { ReactComponent as IconUnchecked } from "@/assets/svgs/checkbox-unchecked.svg";
import { ReactComponent as IconChevron } from "@/assets/svgs/chevron-down-small.svg";
import { ReactComponent as IconSearch } from "@/assets/svgs/icon-search.svg";
import InputSuffixIcon from "@/components/InputSuffixIcon";
import Popover from "@/components/Popover";
import classname from "classnames";
import { useEffect, useRef, useState } from "react";
import MultiClamp from "react-multi-clamp";
import Search from '@/componentsDS/Search/index';
import { feature, features } from "@/utils/feature-verifier";

const MultiCheckDropdown = ({
	placeholder,
	selectionList,
	handleSelectionClick,
	disable,
	filterSearch,
	prefix,
	suffix,
	veridaptSuffix = false,
}) => {
	const [isvisible, setIsVisible] = useState(false);
	const [filterValue, setFilterValue] = useState("");
	const [selectedMultiItem, setSelectedMultiItem] = useState([]);

	const referenceRef = useRef(null);
	const popperRef = useRef(null);

	const sameWidth = {
		name: "sameWidth",
		enabled: true,
		phase: "beforeWrite",
		requires: ["computeStyles"],
		fn: ({ state }) => {
			state.styles.popper.width = `${state.rects.reference.width}px`;
		},
		effect: ({ state }) => {
			state.elements.popper.style.width = `${state.elements.reference.offsetWidth}px`;
		},
	};

	useEffect(() => {
		document.addEventListener("mousedown", handleDocumentClick);
		return () => {
			document.removeEventListener("mousedown", handleDocumentClick);
		};
	}, []);

	function handleDocumentClick(event) {
		if (
			referenceRef.current?.contains(event.target) ||
			popperRef.current?.contains(event.target)
		) {
			return;
		}
		setIsVisible(false);
		setFilterValue("");
	}

	const multiSelectDropdownClass = classname({
		"single-select-dropdown__container": true,
		"single-select-dropdown__container--active": isvisible && !disable,
		"single-select-dropdown__container--inactive": !isvisible && !disable,
		"single-select-dropdown__container--disable": disable,
	});

	useEffect(() => {
		const selectedItem = selectionList?.filter(
			(item) => item.$selected === true
		);
		setSelectedMultiItem(selectedItem);
	}, [selectionList]);

	function handleMultiDropdownClick() {
		if (disable) {
			return;
		}
		setIsVisible(!isvisible);
		setFilterValue("");
	}

	function handleMultiItemSelect(option) {
		const updatedSelectionList = [...selectionList];

		if (option.type) {
			updatedSelectionList?.map((item) => {
				if (item.id === option.id && item.type === option.type) {
					item.$selected = !item.$selected;
				}
				return item;
			});
			return handleSelectionClick(updatedSelectionList);
		}

		updatedSelectionList?.map((item) => {
			if (item.id === option.id) {
				item.$selected = !item.$selected;
			}
			return item;
		});

		handleSelectionClick(updatedSelectionList);
	}

	function handleSelectAll() {
		const updatedSelectionList = [...selectionList];

		if (selectionList.every((item) => item.$selected === true)) {
			updatedSelectionList.forEach((item) => (item.$selected = false));

			return handleSelectionClick(updatedSelectionList);
		}
		updatedSelectionList.forEach((item) => (item.$selected = true));
		handleSelectionClick(updatedSelectionList);
	}

	return (
		<div className="single-select-dropdown-wrapper">
			<Popover
				visible={isvisible}
				options={{
					modifiers: [
						{ name: "offset", options: { offset: [0, 5] } },
						sameWidth,
					],
				}}
			>
				<Popover.Trigger>
					<div
						className={multiSelectDropdownClass}
						ref={referenceRef}
						onClick={handleMultiDropdownClick}
					>
						<>
							<div className="single-select-dropdown__text">
								{selectedMultiItem?.length > 0 ? (
									<div className="single-select-dropdown__selection-text">
										{`${selectedMultiItem?.length} Selected`}
									</div>
								) : (
									<div
										className={`${
											disable
												? "single-select-dropdown__disable-text"
												: "single-select-dropdown__placeholder-text"
										}`}
									>
										{placeholder}
									</div>
								)}
							</div>
							<div className="single-select-dropdown__icon">
								{isvisible ? (
									<div className="multi-check-dropdown__done-text">
										Done
									</div>
								) : (
									<IconChevron />
								)}
							</div>
						</>
					</div>
				</Popover.Trigger>

				<Popover.Content>
					<div
						className="single-select-dropdown__selection-container"
						ref={popperRef}
					>
						{filterSearch && (
							<div className="single-select-dropdown__searchbox">
								<Search
									value={filterValue}
									placeholder="Search"
									onChange={(e) => setFilterValue(e?.target.value)}
									onCancel={() => setFilterValue('')}
									variant='filled'
								/>
							</div>
						)}

						<div className="multicheck-dropdown__wrapper">
							<div
								className="multicheck-dropdown__total-count-selection"
								onClick={handleSelectAll}
								data-testid="select-all-item"
							>
								{selectionList?.length > 0 && (
									<>
										{selectionList?.every(
											(item) => item.$selected === false
										) && <IconUnchecked />}
										{selectionList?.every(
											(item) => item.$selected === true
										) && <IconChecked />}
										{!selectionList?.every(
											(item) => item.$selected === true
										) &&
											!selectionList?.every(
												(item) =>
													item.$selected === false
											) && <IconPartialChecked />}
									</>
								)}

								<div className="multicheck-dropdown__total-count-text">{`Total Available: ${String(
									selectionList?.length
								).padStart(2, "0")}`}</div>
							</div>

							{selectionList
								?.filter((item) =>
									item.name
										.toLowerCase()
										.includes(filterValue.toLowerCase())
								)
								.map((option, index) => (
									<div
										key={`id-${option.id}-${index}`}
										className={`multicheck-dropdown__selection-item ${
											option.$selected
												? "multicheck-dropdown__selected-item"
												: ""
										}`}
										onClick={() =>
											handleMultiItemSelect(option)
										}
									>
										<div
											className="multicheck-dropdown__checkbox"
											data-testid="multi-check-box"
										>
											{option.$selected ? (
												<IconChecked />
											) : (
												<IconUnchecked />
											)}
										</div>
										<div className="multicheck-dropdown__selection-item-wrapper">
											{prefix && (
												<div className="multicheck-dropdown__selection-item-prefix">
													{prefix}
												</div>
											)}

											<div className="multicheck-dropdown__selection-item-name">
												<MultiClamp clamp={2}>
													{option.name}
												</MultiClamp>
											</div>
										</div>

										{suffix && (
											<div className="multicheck-dropdown__selection-item-suffix">
												{suffix}
											</div>
										)}

										{veridaptSuffix && (
											<div className="multicheck-dropdown__selection-item-suffix">
												{option.type === "SEND"
													? "Sending"
													: "Receiving"}
											</div>
										)}
									</div>
								))}
						</div>
					</div>
				</Popover.Content>
			</Popover>
		</div>
	);
};

export default MultiCheckDropdown;
